import { useTranslation } from 'react-i18next';
import type { ColumnConfig } from '@/components/ui';
import { AccountsTable as SharedAccountsTable } from '../Shared/AccountsTable';
import { IdentityCell, StatusChip, type CellTone } from '../Shared/cells';
import { isPendingFirstLogin, type AccountSortDirection, type AccountSortField, type KeycloakAccountRow } from '../types';
import type { RealmAccountColumnKey, RealmAccountViewsVM } from '../useRealmAccountViews';
import { accountDisplayName } from '../Profile/accountDisplayName';
import cells from '../accountsCells.module.css';

/**
 * EAA-1839: the realm board's column widths are a BUDGET, not five independent
 * numbers, and the arithmetic is written down because getting it wrong makes a
 * column 0px wide. `DataTable` renders antd with `table-layout: fixed` (its
 * default `scroll: { x: 900 }`), under which a column with no declared width
 * gets only the LEFTOVER space — and when the declared widths already sum to the
 * table's width, the leftover is zero. That is exactly how the org board's
 * identity column collapsed to 0px earlier in this ticket, avatar overlapping
 * its own text and a 199px-tall header wrapping one letter per line.
 *
 * MEASURED in the running app at 1366x768 with the sidebar rail:
 * `.acc-table-scroll` is 988px and the table inside it 978px. The chrome takes
 * ~32px (EAA-1839: `compactSelectionColumn`, hugging the checkbox instead of
 * antd's default width-less min-content column — see `DataTable.types.ts`)
 * + 120px (`AccountsTable`'s `actionsWidth`), leaving ~826px for the SIX data
 * columns. At 1440x900 the same measurement is 1062/1052, i.e. ~895px — every
 * column below simply gets proportionally wider, so 1366 is the binding case and
 * the only one the arithmetic has to close.
 *
 * THE BUDGET IS DECLARED FOR ALL SIX, AND SIZED FOR FIVE. That double
 * requirement is the whole reason these numbers look small:
 *
 *     username 173 + email 158 + placement 148 + app_roles 94
 *   + status 133 + last_login 92                            = 798
 *   + selection 32 + actions 120                            = 950  <  978  ✓
 *
 * EAA-1839: `username` absorbed the 5px freed by shrinking the selection
 * column from ~37 to 32 (was 168) — the total stays 950, so every proportional
 * figure below this point is unchanged.
 *
 * `last_login` starts HIDDEN (see `HIDEABLE_COLUMNS` in `useRealmAccountViews`),
 * so the usual case declares 701 against ~821 available, and `table-layout:
 * fixed` distributes the 120px of slack PROPORTIONALLY (verified against the org
 * board: 920 declared in a 988 table rendered as 238/165/134/145/145/124, each
 * +3.4%). The five visible columns therefore render at roughly
 * username 192 / email 180 / placement 169 / app_roles 107 / status 152 — which
 * is what actually fixes the three row-quality complaints this pass exists for.
 * Turning `last_login` back on drops the factor to ~1.04 (measured: 173 / 163 /
 * 153 / 97 / 137 / 95, summing to the table's 978 with nothing clipped and the
 * status still on two lines). What it DOES cost, measured rather than guessed, is
 * height: "Đăng nhập cuối" wraps to three lines at 95px, taking the header and
 * the tallest row from 68/70px to 90/90px. That is the price of the sixth column
 * and the reason it is not the default — not a defect to be widened away, because
 * there is no width left in the budget to widen it with.
 *
 * WHY THE SUM MUST NEVER EXCEED THE AVAILABLE WIDTH: `.acc-table-scroll` is
 * `overflow-x: hidden` (measured), so a table wider than its container is
 * CLIPPED, not scrolled — the rightmost columns would simply disappear. This is
 * a harder constraint than "a column might collapse to 0px", which is the
 * failure the org board hit and which the comment here used to warn about
 * exclusively.
 *
 * WHAT THE THREE COMPLAINTS COST, individually:
 *  - `status` went 95 → 133 because "Chờ đăng nhập lần đầu" measures 156px of
 *    text + 15px of dot and gap. At a rendered 156 (five columns) it takes two
 *    lines; the CSS fix in `15-accounts.css` is what makes those two lines
 *    possible at all — see `.acc-status` there for why the old inline-flex box
 *    wrapped one WORD per line no matter how wide the column was.
 *  - `email` went 122 → 158 (rendered ~180) because real addresses here measure
 *    109–197px and the old 82px of content clipped nearly all of them.
 *  - `placement` went 155 → 148 and yet got MUCH roomier, because the Level name
 *    moved to the cell's second line (see `PlacementCell`) instead of being
 *    concatenated onto the org name. The org names measure 32–206px; the
 *    concatenated string measured 402–576px, so no width in this budget was ever
 *    going to fit it.
 *  - `app_roles` and `last_login` paid for it. Both are sized to their CONTENT
 *    rather than their heading: every row in this realm renders "Chưa có quyền" /
 *    "Chưa ghi nhận", short plain text that wraps rather than clipping, and a
 *    role-key chip list wraps by design (`.acc-approles`). Their HEADINGS wrap
 *    too — see the height note above for what that costs when `last_login` is
 *    switched back on.
 *
 * THE HEADER IS TWO LINES TALL (~66px) and stays that way — a wrapped heading is
 * read once, a clipped username is read on every row. Fitting all six Vietnamese
 * headings on one line needs 803px of the 821 available and would leave the
 * identity column at 126px.
 *
 * Every column carries a width ON PURPOSE, including the identity column: only
 * columns with a numeric width become resizable (see `AccountsTable`'s `resize`
 * prop), and a width-less identity column is the one that collapses when a drag
 * grows its neighbours. `useResizableColumns` enforces an 80px floor per column,
 * so a drag can shrink the budget but never zero a column.
 *
 * If a column is ADDED here, re-measure and re-balance — do not just append a
 * number.
 */
const COLUMN_WIDTHS = {
    username: 173,
    email: 158,
    placement: 148,
    app_roles: 94,
    status: 133,
    last_login: 92,
} as const;

/*
 * EAA-1839 (2026-08-21, user request): the identity column's primary line is
 * `accountDisplayName()` — `Profile/accountDisplayName.ts`, ALREADY the chain
 * this column needs (`first_name last_name` -> `username` -> `email` -> "—") and
 * already used by `useAccountProfilePage`, `ResetAccountDrawer` and
 * `AppDetailAccountsTab`. A fourth hand-rolled copy was written here first and
 * deleted: the point of a display-name rule is that every surface agrees on it.
 *
 * AND IT RESOLVES TO REAL NAMES, which the first attempt at this column assumed
 * it could not. `KeycloakAccountRow`'s docstring claimed the list read carried no
 * name field; measuring the live endpoint disproved it — `GET /api/v1/accounts`
 * returns `first_name`/`last_name` on every row. The stale claim is corrected in
 * `../types.ts`, where the two fields are now declared (OPTIONAL: Keycloak
 * requires neither). No serializer change is needed and none was requested.
 *
 * The fallback order still matters, because rows in this realm have the names
 * empty, and rows have them identical ("phuvanschool phuvanschool"). NOTHING IS
 * DERIVED to paper over that — no email local part, no split-on-dot of the
 * username, no title-casing. Inventing a person's name on a security screen
 * is worse than showing a machine identifier.
 */
/**
 * EAA-1820: not a component, so it takes the already-resolved `t` from its
 * caller instead of calling `useTranslation` itself (hooks may only run in
 * components/hooks).
 */
function lastLoginLabel(
    lastLogin: number | null | undefined,
    available: boolean,
    t: (key: string) => string
): string {
    if (!available) return t('console.accounts.table.lastLoginUnavailable');
    if (!lastLogin) return t('console.accounts.table.lastLoginNotRecorded');
    return new Date(lastLogin).toLocaleDateString('vi-VN');
}

function PlacementCell({ placement }: { placement?: KeycloakAccountRow['placement'] }) {
    const { t } = useTranslation();
    if (!placement) {
        return (
            <span className={cells.unassigned}>
                {t('console.accounts.table.placementUnassigned')}
            </span>
        );
    }
    /*
     * EAA-1839: the org name and the Level name are TWO LINES, not one
     * concatenated "{org} · {level}" string.
     *
     * The concatenation is what produced "Tiểu học Phù Vân · Unass…" — and it
     * clipped the wrong half. Measured in this realm: the org names run 32–206px,
     * the joined string 402–576px, and every `level_name` is the same
     * auto-generated "Unassigned ({app}, auto-synced, pending role mapping)". So
     * the tail that survived the clamp was identical on every row while the org
     * name — the only part that distinguishes rows — was the part being cut. No
     * width available in `COLUMN_WIDTHS` was ever going to fit both on one line.
     *
     * The Level is not dropped, for the same reason the org board's Level popover
     * exists: it is thin information, not no information. It moves to a muted
     * second line, which costs zero row height because the identity column beside
     * it is already two lines tall, and carries a native `title` for the full
     * value. The org board's answer to the same problem was to show the SHORT
     * `role_key` instead of `level_name`; this row shape has no `role_key`
     * (`placement` carries `organization_name`/`level_name`/`count` and nothing
     * else — see `types.ts`), and its resolved role keys already have their own
     * "Vai trò ứng dụng" column, so there is nothing shorter to substitute here.
     *
     * "+N đơn vị khác" shares that second line rather than claiming a third: it
     * is a one-token counter, and a three-line cell would be the tallest thing on
     * the row.
     */
    return (
        <span className={cells.placement}>
            <span className={cells.placementWhere} title={placement.organization_name}>
                {placement.organization_name}
            </span>
            {(placement.level_name || placement.count > 1) && (
                <span className={cells.placementMeta}>
                    {placement.level_name && (
                        <span className={cells.placementLevel} title={placement.level_name}>
                            {placement.level_name}
                        </span>
                    )}
                    {placement.count > 1 && (
                        <span className={cells.placementMore}>
                            {t('console.accounts.table.placementMoreCount', {
                                count: placement.count - 1,
                            })}
                        </span>
                    )}
                </span>
            )}
        </span>
    );
}

function AppRolesCell({ row }: { row: KeycloakAccountRow }) {
    const { t } = useTranslation();
    const roles = row.app_roles ?? [];
    if (roles.length > 0) {
        return (
            <span className={cells.approles}>
                {roles.map((role) => (
                    <code className={cells.approle} key={role}>
                        {role}
                    </code>
                ))}
            </span>
        );
    }
    if (!row.placement) return <span className="portal-accounts-unavailable-cell">—</span>;
    return <span className={cells.unassigned}>{t('console.accounts.table.noPermission')}</span>;
}

interface Props {
    rows: KeycloakAccountRow[];
    selected: Set<string>;
    onSelectedChange: (next: Set<string>) => void;
    sortField: AccountSortField;
    sortDirection: AccountSortDirection;
    onSort: (field: AccountSortField) => void;
    /** The "Xem" button AND — until `Shared/AccountsTable` grows its own
     *  `onRowClick`, which it must for the row click to differ — the `<tr>`
     *  click. Navigates to `/console/accounts/:sub`. */
    onViewDetail: (row: KeycloakAccountRow) => void;
    /** EAA-1839 (2026-08-21, user request): the quick drill-in — opens
     *  `AccountDetailModal` over the list instead of leaving it. */
    onOpenDetailModal: (row: KeycloakAccountRow) => void;
    /** EAA-1839: presentation state (visible columns + row density) — passed as
     *  the whole hook result per CLAUDE.md's "hook model prop" rule, since the
     *  alternative is three more individual props on an already-wide surface. */
    views: RealmAccountViewsVM;
    onResetOne: (row: KeycloakAccountRow) => void;
    onDeleteOne: (row: KeycloakAccountRow) => void;
    /** EAA-1820 (frame 84): opens the "Gán Level" dialog for this identity. */
    onAssignLevel: (row: KeycloakAccountRow) => void;
    /** EAA-1820 (frame 86): opens the disable/re-enable dialog for this identity. */
    onDeactivate: (row: KeycloakAccountRow) => void;
    canReset: boolean;
    canDelete: boolean;
    /** EAA-1820: coarse proxy for "may grant Level assignments" — see
     *  `useAssignLevelToAccount`'s docstring for why there is no single
     *  capability string to check upfront on this realm-wide screen. */
    canAssignLevel: boolean;
    /** `console.accounts.lock` — gates the enable/disable toggle. */
    canLock: boolean;
    lastLoginAvailable: boolean;
}

/**
 * EAA-1820: the realm accounts columns, rendered through the ONE shared
 * `shared/AccountsTable`. Column CONTENT (placement, app roles, status/last-
 * login derivation) stays here because it is realm-specific domain logic; the
 * table chrome (DataTable, selection, sort caret, "Xem" + kebab, single scroll
 * region) is now shared with the org screen's `ScopedAccountsTable.tsx`.
 *
 * EAA-1804: renamed from `AccountsTable` to `AccountsListTable` — the folder
 * used to hold two components both exported as `AccountsTable` (this
 * realm-column wrapper and the canonical `shared/AccountsTable`), which this
 * file's own import alias (`SharedAccountsTable`) already worked around at
 * every call site. The canonical name now belongs solely to `shared/AccountsTable`.
 */
export function AccountsListTable({
    rows,
    selected,
    onSelectedChange,
    sortField,
    sortDirection,
    onSort,
    onViewDetail,
    onOpenDetailModal,
    views,
    onResetOne,
    onDeleteOne,
    onAssignLevel,
    onDeactivate,
    canReset,
    canDelete,
    canAssignLevel,
    canLock,
    lastLoginAvailable,
}: Props) {
    const { t } = useTranslation();

    /**
     * EAA-1839: the full column set, before the "Hiển thị" popover's visibility
     * choices are applied. Declared as ONE array rather than a chain of spread
     * conditionals so the width budget above stays readable — the filter below is
     * the only thing that removes a column, and the identity column is exempt from
     * it (see `HIDEABLE_COLUMNS` in `useRealmAccountViews.ts` for why).
     */
    const allColumns: ColumnConfig<KeycloakAccountRow>[] = [
        {
            key: 'username',
            title: t('console.accounts.table.columnAccount'),
            sorter: true,
            width: COLUMN_WIDTHS.username,
            render: (_value, row) => {
                const primary = accountDisplayName(row);
                return (
                    <IdentityCell
                        primary={primary}
                        // EAA-1839: the username goes on the second line ONLY when the
                        // first line is not already showing it — printing the same
                        // string twice reads as a rendering bug, which is exactly what
                        // the org board did before this guard. Today `primary` IS the
                        // username for almost every row (see `identityPrimaryOf`), so
                        // this line is normally absent; it appears the moment the list
                        // serializer starts returning a real name.
                        secondary={primary === row.username ? undefined : row.username}
                        onClick={() => onViewDetail(row)}
                    />
                );
            },
        },
        {
            // EAA-1839 (2026-08-21, user request): email split out of the identity
            // cell into its own column, so it can be scanned down on its own. Fully
            // available — `KeycloakUserSerializer` carries `email` directly.
            key: 'email',
            title: t('console.accounts.table.columnEmail'),
            sorter: true,
            width: COLUMN_WIDTHS.email,
            render: (_value, row) =>
                row.email ? (
                    <span className={cells.emailCell} title={row.email}>
                        {row.email}
                    </span>
                ) : (
                    <span className="portal-accounts-unavailable-cell">
                        {t('console.accounts.table.noEmail')}
                    </span>
                ),
        },
        {
            key: 'placement',
            title: t('console.accounts.table.columnPlacement'),
            width: COLUMN_WIDTHS.placement,
            render: (_value, row) => <PlacementCell placement={row.placement} />,
        },
        {
            key: 'app_roles',
            title: t('console.accounts.table.columnAppRoles'),
            width: COLUMN_WIDTHS.app_roles,
            render: (_value, row) => <AppRolesCell row={row} />,
        },
        {
            key: 'status',
            title: t('console.accounts.table.columnStatus'),
            sorter: true,
            width: COLUMN_WIDTHS.status,
            render: (_value, row) => {
                const tone: CellTone = !row.enabled ? 'off' : isPendingFirstLogin(row) ? 'warn' : 'ok';
                const label = !row.enabled
                    ? t('console.accounts.table.statusLocked')
                    : isPendingFirstLogin(row)
                      ? t('console.accounts.table.statusPendingFirstLogin')
                      : t('console.accounts.table.statusActive');
                return <StatusChip tone={tone} label={label} />;
            },
        },
        {
            key: 'last_login',
            title: t('console.accounts.table.columnLastLogin'),
            width: COLUMN_WIDTHS.last_login,
            render: (_value, row) => lastLoginLabel(row.last_login, lastLoginAvailable, t),
        },
    ];

    const columns = allColumns.filter(
        (column) =>
            column.key === 'username' || views.isColumnVisible(column.key as RealmAccountColumnKey)
    );

    return (
        <SharedAccountsTable<KeycloakAccountRow>
            rows={rows}
            columns={columns}
            rowKey="sub"
            selectedKeys={[...selected]}
            onSelectedKeysChange={(keys) => onSelectedChange(new Set(keys))}
            sortedInfo={{ field: sortField, order: sortDirection === 'asc' ? 'ascend' : 'descend' }}
            onSortChange={(ordering) => onSort(ordering.replace(/^-/, '') as AccountSortField)}
            onViewDetail={onViewDetail}
            // EAA-1839: the split the user asked for — a ROW click opens the detail
            // modal over the list, while "Xem" navigates to the profile route. Both
            // are deliberate; see `AccountsPage`'s docstring for the frame-72
            // reversal that made the route the button's destination.
            onRowClick={onOpenDetailModal}
            rowActionsAriaLabel={(row) =>
                t('console.accounts.table.rowActionsAriaLabel', { username: row.username })
            }
            /* EAA-1839: per-viewer persisted column widths under this table's OWN
               key — `ui:colwidths:realm-accounts`, distinct from the org board's
               `ui:colwidths:scoped-accounts`. The kit requires per-TABLE keys: the
               two boards have different column sets, so one shared key would let
               each overwrite the other's widths. No `version` is set yet; bump it
               only if the column set is reshaped enough that stored widths would
               land on the wrong columns. */
            resize={{ storageKey: 'realm-accounts' }}
            /* EAA-1839: "Mật độ" — the class is the only thing that changes; see
               `.acc-table--compact` in `15-accounts.css`. */
            className={`acc-table--${views.density}`}
            rowActions={(row) => [
                {
                    // EAA-1839 (2026-08-21, user request): the QUICK drill-in — opens
                    // `AccountDetailModal` over the list. Distinct from the "Xem"
                    // button beside it, which leaves the list for
                    // `/console/accounts/:sub`. The two are deliberately different
                    // destinations, not a duplicate.
                    key: 'detail',
                    label: t('console.accounts.table.viewDetailAction'),
                    onSelect: () => onOpenDetailModal(row),
                },
                ...(canAssignLevel
                    ? [{ key: 'assign-level', label: t('console.accounts.table.assignLevelAction'), onSelect: () => onAssignLevel(row) }]
                    : []),
                ...(canReset
                    ? [{ key: 'reset', label: t('console.accounts.table.resetAction'), onSelect: () => onResetOne(row) }]
                    : []),
                ...(canDelete
                    ? [
                          {
                              key: 'delete',
                              label: t('console.accounts.table.deleteAction'),
                              danger: true,
                              onSelect: () => onDeleteOne(row),
                          },
                      ]
                    : []),
                ...(canLock
                    ? [
                          {
                              key: 'deactivate',
                              label: row.enabled
                                  ? t('console.accounts.table.deactivateAction')
                                  : t('console.accounts.table.reactivateAction'),
                              danger: row.enabled,
                              onSelect: () => onDeactivate(row),
                          },
                      ]
                    : []),
            ]}
            emptyText={t('console.accounts.table.emptyText')}
        />
    );
}

export default AccountsListTable;
